/*
 * Cart-level in-cart upsells.
 *
 * Liquid supplies candidates only from `custom.in_cart_upsells`. This
 * controller validates the live cart, exposes one candidate at a time, and
 * attaches every accepted offer to its qualifying parent through the shared
 * relationship contract. It is active for eligible current-format carts and
 * fails closed for incompatible persisted legacy groups.
 */
(function registerMagBakCartUpsells() {
  'use strict';

  if (!window.customElements || window.customElements.get('mb-cart-upsells')) return;

  const ajaxUrl = (url) => {
    const [path, query] = String(url || '').split('?');
    const jsPath = path.endsWith('.js') ? path : `${path}.js`;
    return query ? `${jsPath}?${query}` : jsPath;
  };

  const requestJson = async (url, options = {}) => {
    const response = await fetch(ajaxUrl(url), {
      ...options,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        ...(options.headers || {}),
      },
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload.status) {
      const error = new Error(payload.description || payload.message || 'The cart could not be updated.');
      error.payload = payload;
      throw error;
    }
    return payload;
  };

  const currentCart = () => requestJson(window.routes?.cart_url || '/cart');
  const changeLine = (key, quantity, properties) => requestJson(window.routes?.cart_change_url || '/cart/change', {
    method: 'POST',
    body: JSON.stringify({ id: key, quantity, ...(properties ? { properties } : {}) }),
  });
  const addLine = (item) => requestJson(window.routes?.cart_add_url || '/cart/add', {
    method: 'POST',
    body: JSON.stringify({ items: [item] }),
  });

  const relationshipWriterEnabled = () => {
    return Boolean(window.MagBakCartRelationship);
  };

  const lineProperties = (line) => ({ ...(line?.properties || {}) });
  const relationshipLines = (cart) => (cart?.items || []).map((item) => ({
    key: item.key,
    quantity: Number(item.quantity || 0),
    properties: lineProperties(item),
  }));
  const matchingParent = (cart, key) => (cart?.items || []).find((item) => item.key === key) || null;

  const alreadyPersonalized = (cart, parent, serviceVariantId) => {
    const contract = window.MagBakCartRelationship;
    const parentRelation = contract.safeParseLink(parent?.properties?._mb_link || '');
    return (cart?.items || []).some((item) => {
      if (String(item.variant_id || item.id) !== String(serviceVariantId)) return false;
      const childRelation = contract.safeParseLink(item.properties?._mb_link || '');
      if (parentRelation.ok && childRelation.ok) {
        return childRelation.link.group === parentRelation.link.group
          && childRelation.link.parent === parentRelation.link.node
          && childRelation.link.kind === contract.KINDS.PERSONALIZATION;
      }
      return !parentRelation.ok
        && !childRelation.ok
        && String(item.properties?._gid || '') === String(parent?.sku || '');
    });
  };

  const standardOfferAlreadyInCart = (cart, productId) => (cart?.items || []).some(
    (item) => String(item.product_id || '') === String(productId || '')
  );

  const findAddedLine = (payload, variantId, link) => (payload?.items || []).find(
    (item) => String(item.variant_id || item.id) === String(variantId)
      && String(item.properties?._mb_link || '') === String(link || '')
  ) || null;

  const restoreOriginalParent = async (original, currentKey) => {
    if (!original) return;
    await changeLine(currentKey || original.key, original.quantity, lineProperties(original));
  };

  class MagBakCartUpsells extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.candidates = Array.from(this.querySelectorAll('mb-cart-upsell-candidate'));
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('change', (event) => this.onVariantChange(event));
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && event.target.matches('input[name="personalization"]')) {
          this.submitPersonalization(event.target.closest('mb-cart-upsell-candidate'), event);
        }
      });
      this.activate();
    }

    async activate() {
      if (!relationshipWriterEnabled() || !this.candidates.length) return;
      try {
        const cart = await currentCart();
        const eligible = this.candidates.find((candidate) => this.isEligible(candidate, cart));
        if (!eligible) return;
        this.candidates.forEach((candidate) => { candidate.hidden = candidate !== eligible; });
        this.hidden = false;
      } catch (error) {
        console.warn('[MagBak in-cart upsells] Offer activation failed closed.', error);
      }
    }

    isEligible(candidate, cart) {
      const parent = matchingParent(cart, candidate.dataset.parentKey);
      if (!parent) return false;
      const relationshipEligibility = window.MagBakCartRelationship.attachmentEligibility(
        relationshipLines(cart),
        parent.key
      );
      if (!relationshipEligibility.eligible) return false;
      if (candidate.dataset.kind === 'personalization') {
        return !alreadyPersonalized(cart, parent, candidate.dataset.offerVariantId);
      }
      return !standardOfferAlreadyInCart(cart, candidate.dataset.offerProductId);
    }

    onClick(event) {
      const candidate = event.target.closest('mb-cart-upsell-candidate');
      if (!candidate) return;
      if (event.target.closest('.mb-cart-upsell__submit')) this.submitPersonalization(candidate, event);
      if (event.target.closest('.mb-cart-upsell__add')) this.submitStandard(candidate, event);
    }

    onVariantChange(event) {
      if (!event.target.matches('.mb-cart-upsell__variant select')) return;
      const candidate = event.target.closest('mb-cart-upsell-candidate');
      const option = event.target.selectedOptions?.[0];
      const price = candidate?.querySelector('.mb-cart-upsell__add strong');
      if (candidate && option) candidate.dataset.offerVariantId = option.value;
      if (price && option?.dataset.price) price.textContent = `+${option.dataset.price}`;
    }

    setBusy(candidate, busy) {
      candidate.querySelectorAll('button, input, select').forEach((control) => {
        control.disabled = busy;
      });
      candidate.toggleAttribute('aria-busy', busy);
    }

    showError(candidate, message) {
      const error = candidate.querySelector('.mb-cart-upsell__error');
      if (!error) return;
      error.textContent = message;
      error.hidden = !message;
    }

    refreshCart() {
      if (this.closest('.mb-redesign-cart')) {
        window.location.reload();
        return;
      }
      const cartItems = this.closest('cart-drawer-items, cart-items')
        || document.querySelector('cart-drawer-items, cart-items');
      if (typeof cartItems?.onCartUpdate === 'function') cartItems.onCartUpdate();
    }

    async prepareParent(cart, parent, kind) {
      const contract = window.MagBakCartRelationship;
      return kind === 'personalization'
        ? contract.planPersonalizationAttachment(relationshipLines(cart), parent.key)
        : contract.planDependentAttachment(relationshipLines(cart), parent.key);
    }

    async applyParentPlan(originalParent, plan) {
      let changedParentKey = '';
      let addedSplitParentKey = '';
      if (plan.requiresQuantitySplit) {
        await changeLine(originalParent.key, plan.remainderQuantity);
        const clonedProperties = { ...lineProperties(originalParent), _mb_link: plan.splitParentLink };
        const parentAdd = await addLine({
          id: originalParent.variant_id || originalParent.id,
          quantity: 1,
          properties: clonedProperties,
          ...(originalParent.selling_plan_allocation?.selling_plan?.id
            ? { selling_plan: originalParent.selling_plan_allocation.selling_plan.id }
            : {}),
        });
        const addedParent = findAddedLine(parentAdd, originalParent.variant_id || originalParent.id, plan.splitParentLink);
        addedSplitParentKey = addedParent?.key || '';
        if (!addedSplitParentKey) throw new Error('The personalized item could not be separated safely.');
      } else if (plan.requiresParentRewrite) {
        const changedCart = await changeLine(originalParent.key, originalParent.quantity, {
          ...lineProperties(originalParent),
          _mb_link: plan.parentLink,
        });
        changedParentKey = findAddedLine(
          changedCart,
          originalParent.variant_id || originalParent.id,
          plan.parentLink
        )?.key || '';
        if (!changedParentKey) throw new Error('The cart item could not be prepared safely.');
      }
      return { changedParentKey, addedSplitParentKey };
    }

    async recover(originalParent, parentMutation, plannedChildLink) {
      if (plannedChildLink) {
        const recoveryCart = await currentCart();
        const orphan = (recoveryCart.items || []).find(
          (item) => String(item.properties?._mb_link || '') === plannedChildLink
        );
        if (orphan) await changeLine(orphan.key, 0);
      }
      if (parentMutation?.addedSplitParentKey) await changeLine(parentMutation.addedSplitParentKey, 0);
      if (originalParent && (parentMutation?.addedSplitParentKey || parentMutation?.changedParentKey)) {
        await restoreOriginalParent(originalParent, parentMutation.changedParentKey || originalParent.key);
      }
    }

    async submitPersonalization(candidate, event) {
      event.preventDefault();
      const input = candidate.querySelector('input[name="personalization"]');
      const value = String(input?.value || '').trim().toUpperCase();
      if (input) input.value = value;
      if (candidate.hasAttribute('aria-busy') || !input?.reportValidity()) return;
      if (!value) {
        input.setCustomValidity('Enter the text to engrave.');
        input.reportValidity();
        return;
      }
      input.setCustomValidity('');
      await this.submitCandidate(candidate, {
        kind: 'personalization',
        customerProperty: { key: input.dataset.propertyKey || 'Engrave Text', value },
      });
    }

    async submitStandard(candidate, event) {
      event.preventDefault();
      if (candidate.hasAttribute('aria-busy')) return;
      await this.submitCandidate(candidate, { kind: 'standard' });
    }

    async submitCandidate(candidate, options) {
      this.showError(candidate, '');
      this.setBusy(candidate, true);
      let originalParent = null;
      let parentMutation = null;
      let plannedChildLink = '';
      try {
        const cart = await currentCart();
        originalParent = matchingParent(cart, candidate.dataset.parentKey);
        if (!originalParent) throw new Error('This cart item changed. Please try again.');
        if (!this.isEligible(candidate, cart)) {
          candidate.hidden = true;
          await this.activate();
          return;
        }

        const plan = await this.prepareParent(cart, originalParent, options.kind);
        parentMutation = await this.applyParentPlan(originalParent, plan);
        plannedChildLink = plan.childLink;

        const properties = options.kind === 'personalization'
          ? window.MagBakCartRelationship.fulfillmentProperties({
            engravingText: options.customerProperty.value,
            parentSku: originalParent.sku || candidate.dataset.parentSku,
            link: plan.childLink,
          })
          : {
            _mb_link: plan.childLink,
          };

        await addLine({
          id: Number(candidate.dataset.offerVariantId),
          quantity: 1,
          properties,
        });
        candidate.hidden = true;
        this.refreshCart();
      } catch (error) {
        try {
          await this.recover(originalParent, parentMutation, plannedChildLink);
        } catch (recoveryError) {
          console.error('[MagBak in-cart upsells] Automatic recovery failed.', recoveryError);
        }
        console.error('[MagBak in-cart upsells] Add failed.', error);
        this.showError(candidate, error.message || 'This offer could not be added. Please try again.');
      } finally {
        this.setBusy(candidate, false);
      }
    }
  }

  window.customElements.define('mb-cart-upsells', MagBakCartUpsells);
})();
